import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Search, Globe, Users, Briefcase, Clock, ArrowRight, 
  ChevronLeft, ChevronRight, Loader2, Filter, X, 
  BookOpen, Target, Zap, Award
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { useTheme } from '../contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SourcingHubPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme } = useTheme();
  
  // State
  const [techniques, setTechniques] = useState([]);
  const [filters, setFilters] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalTechniques, setTotalTechniques] = useState(0);
  
  // Filter state
  const [selectedCountry, setSelectedCountry] = useState(searchParams.get('country') || 'All');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'All');
  const [selectedTheme, setSelectedTheme] = useState(searchParams.get('theme') || 'All');
  const [selectedDifficulty, setSelectedDifficulty] = useState(searchParams.get('difficulty') || 'All');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [showFilters, setShowFilters] = useState(false);
  
  const techniquesPerPage = 20;
  const bgColor = theme.colors.backgroundDark;
  const accentColor = theme.colors.accent;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedCountry !== 'All') params.set('country', selectedCountry);
    if (selectedCategory !== 'All') params.set('category', selectedCategory);
    if (selectedTheme !== 'All') params.set('theme', selectedTheme);
    if (selectedDifficulty !== 'All') params.set('difficulty', selectedDifficulty);
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (currentPage > 1) params.set('page', currentPage.toString());
    setSearchParams(params);
  }, [selectedCountry, selectedCategory, selectedTheme, selectedDifficulty, debouncedSearch, currentPage, setSearchParams]);

  // Load initial data
  useEffect(() => {
    loadFilters();
    loadStats();
  }, []);

  // Load techniques when filters change
  useEffect(() => {
    loadTechniques();
  }, [selectedCountry, selectedCategory, selectedTheme, selectedDifficulty, debouncedSearch, currentPage]);

  const loadFilters = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/sourcing/filters`);
      const data = await res.json();
      setFilters(data);
    } catch (error) {
      console.error('Error loading filters:', error);
    }
  };

  const loadStats = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/sourcing/stats`);
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadTechniques = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCountry !== 'All') params.set('country', selectedCountry);
      if (selectedCategory !== 'All') params.set('role_category', selectedCategory);
      if (selectedTheme !== 'All') params.set('theme', selectedTheme);
      if (selectedDifficulty !== 'All') params.set('difficulty', selectedDifficulty);
      if (debouncedSearch) params.set('search', debouncedSearch);
      params.set('limit', techniquesPerPage.toString());
      params.set('skip', ((currentPage - 1) * techniquesPerPage).toString());

      const res = await fetch(`${BACKEND_URL}/api/sourcing/techniques?${params.toString()}`);
      const data = await res.json();
      setTechniques(data.techniques || []);
      setTotalTechniques(data.total || 0);
    } catch (error) {
      console.error('Error loading techniques:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSelectedCountry('All');
    setSelectedCategory('All');
    setSelectedTheme('All');
    setSelectedDifficulty('All');
    setSearchQuery('');
    setDebouncedSearch('');
    setCurrentPage(1);
  };

  const hasActiveFilters = selectedCountry !== 'All' || selectedCategory !== 'All' || 
    selectedTheme !== 'All' || selectedDifficulty !== 'All' || debouncedSearch;
  const totalPages = Math.ceil(totalTechniques / techniquesPerPage);

  const countryFlags = {
    'India': '🇮🇳',
    'USA': '🇺🇸',
    'UK': '🇬🇧',
    'EMEA': '🌍',
    'Australia': '🇦🇺'
  };

  const difficultyColors = {
    'Beginner': 'bg-green-500',
    'Intermediate': 'bg-yellow-500',
    'Advanced': 'bg-red-500'
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: bgColor }}>
      <SEOHead 
        title="Sourcing Knowledge Hub | 200+ Free Sourcing Techniques"
        description="Explore 200+ real-world sourcing techniques across countries and industries. Free knowledge library for recruiters, sourcers, and talent acquisition professionals."
        keywords="sourcing techniques, boolean search, talent sourcing, recruitment strategies, passive candidates, recruiter training"
        url="https://venkatswaroop.in/sourcing-hub"
      />
      <Header />
      
      <div className="container mx-auto px-6 py-20">
        {/* Hero Section */}
        <div className="mb-12 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <BookOpen className="h-10 w-10" style={{ color: accentColor }} />
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-white">
              Sourcing Knowledge Hub
            </h1>
          </div>
          <div className="mx-auto h-1 w-32 rounded mb-6" style={{ backgroundColor: accentColor }}></div>
          <p className="mx-auto max-w-3xl text-lg md:text-xl text-white/90 mb-6">
            <span className="font-semibold" style={{ color: accentColor }}>
              Real-World Sourcing Techniques
            </span>
            <br />
            Across Countries & Role Categories
          </p>
          <p className="mx-auto max-w-2xl text-white/70">
            A practitioner-led knowledge base built from 15+ years of global talent acquisition experience.
            Explore proven sourcing strategies that work.
          </p>
        </div>

        {/* Stats Cards */}
        {stats && stats.total_techniques > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
            {Object.entries(stats.by_country || {}).map(([country, count]) => (
              <Card 
                key={country}
                className="bg-white/5 border-none cursor-pointer hover:bg-white/10 transition-all"
                onClick={() => {
                  setSelectedCountry(country);
                  setCurrentPage(1);
                }}
              >
                <CardContent className="p-4 text-center">
                  <div className="text-3xl mb-2">{countryFlags[country] || '🌐'}</div>
                  <div className="text-2xl font-bold text-white">{count}</div>
                  <div className="text-sm text-white/70">{country}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Filters */}
          <div className="lg:col-span-1 space-y-6">
            {/* Mobile Filter Toggle */}
            <Button
              variant="outline"
              className="lg:hidden w-full text-white border-white/30 hover:bg-white/10"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>

            <div className={`space-y-6 ${showFilters ? 'block' : 'hidden lg:block'}`}>
              {/* Search */}
              <Card className="bg-white/10 border-none backdrop-blur-sm">
                <CardContent className="p-6">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Search className="h-5 w-5" />
                    Search
                  </h3>
                  <div className="relative">
                    <Input
                      placeholder="Search techniques..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Country Filter */}
              <Card className="bg-white/10 border-none backdrop-blur-sm">
                <CardContent className="p-6">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Country
                  </h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => { setSelectedCountry('All'); setCurrentPage(1); }}
                      className={`w-full text-left px-4 py-2 rounded-lg transition-all ${
                        selectedCountry === 'All'
                          ? 'text-white font-semibold'
                          : 'text-white/70 hover:text-white hover:bg-white/5'
                      }`}
                      style={selectedCountry === 'All' ? { backgroundColor: accentColor } : {}}
                    >
                      All Countries
                    </button>
                    {filters?.countries?.map((c) => (
                      <button
                        key={c.name}
                        onClick={() => { setSelectedCountry(c.name); setCurrentPage(1); }}
                        className={`w-full text-left px-4 py-2 rounded-lg transition-all flex justify-between ${
                          selectedCountry === c.name
                            ? 'text-white font-semibold'
                            : 'text-white/70 hover:text-white hover:bg-white/5'
                        }`}
                        style={selectedCountry === c.name ? { backgroundColor: accentColor } : {}}
                      >
                        <span>{countryFlags[c.name]} {c.name}</span>
                        <span className="text-sm">({c.count})</span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Role Category Filter */}
              <Card className="bg-white/10 border-none backdrop-blur-sm">
                <CardContent className="p-6">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Briefcase className="h-5 w-5" />
                    Role Category
                  </h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => { setSelectedCategory('All'); setCurrentPage(1); }}
                      className={`w-full text-left px-4 py-2 rounded-lg transition-all ${
                        selectedCategory === 'All'
                          ? 'text-white font-semibold'
                          : 'text-white/70 hover:text-white hover:bg-white/5'
                      }`}
                      style={selectedCategory === 'All' ? { backgroundColor: accentColor } : {}}
                    >
                      All Categories
                    </button>
                    {filters?.role_categories?.map((c) => (
                      <button
                        key={c.name}
                        onClick={() => { setSelectedCategory(c.name); setCurrentPage(1); }}
                        className={`w-full text-left px-4 py-2 rounded-lg transition-all flex justify-between ${
                          selectedCategory === c.name
                            ? 'text-white font-semibold'
                            : 'text-white/70 hover:text-white hover:bg-white/5'
                        }`}
                        style={selectedCategory === c.name ? { backgroundColor: accentColor } : {}}
                      >
                        <span>{c.name}</span>
                        <span className="text-sm">({c.count})</span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Theme Filter */}
              <Card className="bg-white/10 border-none backdrop-blur-sm">
                <CardContent className="p-6">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Sourcing Theme
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    <button
                      onClick={() => { setSelectedTheme('All'); setCurrentPage(1); }}
                      className={`w-full text-left px-4 py-2 rounded-lg transition-all ${
                        selectedTheme === 'All'
                          ? 'text-white font-semibold'
                          : 'text-white/70 hover:text-white hover:bg-white/5'
                      }`}
                      style={selectedTheme === 'All' ? { backgroundColor: accentColor } : {}}
                    >
                      All Themes
                    </button>
                    {filters?.themes?.map((t) => (
                      <button
                        key={t.name}
                        onClick={() => { setSelectedTheme(t.name); setCurrentPage(1); }}
                        className={`w-full text-left px-4 py-2 rounded-lg transition-all flex justify-between ${
                          selectedTheme === t.name
                            ? 'text-white font-semibold'
                            : 'text-white/70 hover:text-white hover:bg-white/5'
                        }`}
                        style={selectedTheme === t.name ? { backgroundColor: accentColor } : {}}
                      >
                        <span>{t.name}</span>
                        <span className="text-sm">({t.count})</span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Difficulty Filter */}
              <Card className="bg-white/10 border-none backdrop-blur-sm">
                <CardContent className="p-6">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Zap className="h-5 w-5" />
                    Difficulty
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <Badge
                      variant={selectedDifficulty === 'All' ? 'default' : 'outline'}
                      className={`cursor-pointer ${
                        selectedDifficulty === 'All'
                          ? 'text-white'
                          : 'text-white/70 border-white/30 hover:text-white bg-transparent'
                      }`}
                      style={selectedDifficulty === 'All' ? { backgroundColor: accentColor } : {}}
                      onClick={() => { setSelectedDifficulty('All'); setCurrentPage(1); }}
                    >
                      All
                    </Badge>
                    {filters?.difficulty_levels?.map((d) => (
                      <Badge
                        key={d}
                        variant={selectedDifficulty === d ? 'default' : 'outline'}
                        className={`cursor-pointer ${
                          selectedDifficulty === d
                            ? 'text-white'
                            : 'text-white/70 border-white/30 hover:text-white bg-transparent'
                        }`}
                        style={selectedDifficulty === d ? { backgroundColor: accentColor } : {}}
                        onClick={() => { setSelectedDifficulty(d); setCurrentPage(1); }}
                      >
                        {d}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Active Filters Banner */}
            {hasActiveFilters && (
              <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 backdrop-blur-sm flex-wrap gap-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-white/70 text-sm">
                    {loading ? 'Searching...' : `${totalTechniques} techniques found`}
                  </span>
                  {debouncedSearch && (
                    <Badge variant="outline" className="text-white border-white/30">
                      &ldquo;{debouncedSearch}&rdquo;
                      <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => setSearchQuery('')} />
                    </Badge>
                  )}
                  {selectedCountry !== 'All' && (
                    <Badge style={{ backgroundColor: accentColor }} className="text-white">
                      {countryFlags[selectedCountry]} {selectedCountry}
                      <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => setSelectedCountry('All')} />
                    </Badge>
                  )}
                  {selectedCategory !== 'All' && (
                    <Badge style={{ backgroundColor: accentColor }} className="text-white">
                      {selectedCategory}
                      <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => setSelectedCategory('All')} />
                    </Badge>
                  )}
                  {selectedTheme !== 'All' && (
                    <Badge style={{ backgroundColor: accentColor }} className="text-white">
                      {selectedTheme}
                      <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => setSelectedTheme('All')} />
                    </Badge>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-white/70 hover:text-white"
                >
                  Clear all
                </Button>
              </div>
            )}

            {/* Loading */}
            {loading && (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-white/50" />
              </div>
            )}

            {/* Techniques Grid */}
            {!loading && techniques.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {techniques.map((tech) => (
                  <Card
                    key={tech.id}
                    className="bg-white/5 border-none backdrop-blur-sm overflow-hidden cursor-pointer hover:bg-white/10 transition-all group"
                    onClick={() => navigate(`/sourcing-hub/${tech.slug}`)}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-2xl">{countryFlags[tech.country] || '🌐'}</span>
                        <Badge variant="outline" className="text-white/70 border-white/30">
                          {tech.country}
                        </Badge>
                        <Badge className={`${difficultyColors[tech.difficulty_level]} text-white text-xs`}>
                          {tech.difficulty_level}
                        </Badge>
                      </div>
                      
                      <h3 className="font-serif text-lg font-bold text-white mb-2 line-clamp-2 group-hover:text-opacity-90">
                        {tech.title}
                      </h3>
                      
                      <div className="flex flex-wrap gap-1 mb-3">
                        <Badge style={{ backgroundColor: `${accentColor}30`, color: accentColor }} className="text-xs">
                          {tech.sourcing_theme}
                        </Badge>
                        {tech.role_categories?.slice(0, 2).map((cat, i) => (
                          <Badge key={i} variant="outline" className="text-white/60 border-white/20 text-xs">
                            {cat}
                          </Badge>
                        ))}
                      </div>
                      
                      {tech.sections?.problem_statement?.content && (
                        <p className="text-white/60 text-sm mb-4 line-clamp-2">
                          {tech.sections.problem_statement.content}
                        </p>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-white/50 text-xs">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {tech.read_time || 5} min
                          </span>
                          <span className="flex items-center gap-1">
                            <Award className="h-3 w-3" />
                            Score: {tech.value_score || 5}/10
                          </span>
                        </div>
                        <ArrowRight className="h-4 w-4 text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Empty State */}
            {!loading && techniques.length === 0 && (
              <div className="text-center py-16">
                <BookOpen className="h-16 w-16 mx-auto text-white/30 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No techniques found</h3>
                <p className="text-white/60 mb-6">
                  {hasActiveFilters 
                    ? 'Try adjusting your filters or search terms'
                    : 'Techniques will appear here once published'}
                </p>
                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    onClick={clearFilters}
                    className="text-white border-white/30 hover:bg-white/10"
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            )}

            {/* Pagination */}
            {!loading && totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-8">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="text-white border-white/30 hover:bg-white/10 disabled:opacity-30"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (currentPage <= 3) pageNum = i + 1;
                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = currentPage - 2 + i;
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className={currentPage === pageNum 
                          ? 'text-white' 
                          : 'text-white border-white/30 hover:bg-white/10'
                        }
                        style={currentPage === pageNum ? { backgroundColor: accentColor } : {}}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="text-white border-white/30 hover:bg-white/10 disabled:opacity-30"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}

            {!loading && techniques.length > 0 && (
              <div className="text-center text-white/50 text-sm pt-4">
                Page {currentPage} of {totalPages} • {totalTechniques} total techniques
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SourcingHubPage;
